<template>
  <div :class="{
    'container-gen': true,
    'with-title': title,
    'act': active,
    }"
    :style='width ? `min-width:${width}; max-width:${width};`: ""'
  >
    <div v-if='title' class='title'>{{title}}</div>
    <FloatingPopover
      v-model="active"
      class="block w-full"
      align="start"
      :getBounds="visibleViewport"
      :trackOnScroll="!IS_IOS"
    >
      <div :class='`input-field ${active ? "act" : ""}`'>
        <button
          ref="fieldRef"
          type="button"
          class="field-shell group w-full gap-1.5 px-3 text-body font-medium
                 [font-family:inherit] focus-visible:outline-none"
          :class="[
            size === 'sm' ? 'min-h-8' : 'min-h-10',
            disabled ? 'is-disabled' : readonly ? 'is-readonly' : 'cursor-pointer hover:border-accent',
            active ? 'border-accent' : '',
          ]"
          @click.stop="readonly || setActive()"
        >
          <div class='content min-w-0 text-left'>
            <div v-if='!modelValue && modelValue !== 0' class="placeholder truncate font-normal text-text-muted">
              {{ readonly ? '—' : placeholder || "Select option" }}
            </div>
            <div v-else class="text truncate text-text">
              <template v-if='!slots.item'>
                <span v-if='selectedOption'>{{$t(selectedOption.label)}}</span>
                <span v-if='selectedOption && selectedOption.sublabel' class='text-sub-label'>
                  {{selectedOption.sublabel}}
                </span>
              </template>
              <slot v-else name="item" :option="selectedOption" :selected="true" />
            </div>
          </div>
          <IconLockClosed16Solid v-if="readonly" class="size-4 shrink-0 self-center text-text-muted" />
          <IconChevronDown
            v-else
            class="cntrl-btn pointer-events-none size-4 shrink-0 self-center
                   transition-[rotate,color] duration-300 ease-out group-hover:text-accent"
            :class="active ? 'rotate-180 text-accent' : 'text-text-muted'"
          />
        </button>
      </div>

      <template #content>
        <div class="popover-panel p-2" :style="{ width: `${fieldWidth}px` }">
          <div class="options v-tracklify-scroll flex max-h-48 flex-col gap-0.5 overflow-y-auto">
            <div
              class="option flex cursor-pointer flex-row items-center gap-1.5 rounded-chip
                     px-2.5 py-1.5 text-text transition-colors duration-150 hover:bg-hover"
              :class="{ act: (modelValue || modelValue === 0) && modelValue === v.id }"
              :title='$t(v.label)'
              @click='clickOption(v.id)' :key='v.id' v-for='v in  inputData'
            >
              <div class="label flex min-w-0 flex-1 flex-col truncate text-text">
                <template v-if='!slots.item'>
                  <div>{{$t(v.label)}}</div>
                  <div v-if="v.sublabel" class="text-sub-label">
                    <span>{{v.sublabel}}</span>
                    <span>{{v.sublabelnote || ""}}</span>
                  </div>
                </template>
                <slot v-else name="item" :option="v" :selected="modelValue === v.id" />
              </div>
            </div>
          </div>
        </div>
      </template>
    </FloatingPopover>
  </div>
</template>

<script setup>
import { ref, computed, watch, useSlots } from 'vue';
import { IconChevronDown, IconLockClosed16Solid } from '@iconify-prerendered/vue-heroicons';
import FloatingPopover from '@/components/elements/FloatingPopover.vue';
import { IS_IOS, visibleViewport } from '@/composables/useFloatingPosition';

// Props
const props = defineProps({
  modelValue: [String, Number],
  inputData: Array,
  placeholder: String,
  disabled: Boolean,
  // shows the picked option as it is, the list never opens
  readonly: Boolean,
  title: String,
  width: String,
  size: {
    type: String,
    default: 'md',
  },
  removeItems: Boolean,
});

// Emits
const emit = defineEmits(['update:modelValue', 'delete']);

// Reactive data
const active = ref(false);
const fieldRef = ref(null);
const fieldWidth = ref(0);
const slots = useSlots();
const selectedOption = computed(() => props.inputData?.find(d => d.id === props.modelValue));

// the panel is teleported out of the field, so it takes the field's width explicitly
watch(active, (isOpen) => {
  if (isOpen) fieldWidth.value = fieldRef.value.offsetWidth;
});

// Methods
const clickOption = (id) => {
  emit('update:modelValue', id);
  active.value = false;
};

const setActive = () => {
  active.value = !active.value;
};
</script>

<style lang='scss' scoped>
.text .text-sub-label {
  margin-left: 20px;
}
.label .text-sub-label {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.text-sub-label {
  color: var(--text-muted);
  font-size: 12px;
  font-weight: normal;
}
.title {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  color: var(--text-soft);
}
.container-gen {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  position: relative;
  &.with-title {
    margin-top: 24px;
  }
}
.input-field {
  display: flex;
  flex-direction: row;
}

i {
  color: var(--text-soft);
  font-weight: bold;
}
.text {
  display: flex;
  flex-direction: row;
  align-items: center;

  span {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}
.placeholder {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.content {
  flex-wrap: wrap;
  display: flex;
  flex-direction: row;
  flex: 1;
}
:deep(.option div.userAvatar) {
  margin-right: 10px;
}
</style>
